import React, { useState, useEffect, useContext } from 'react';
import _ from 'underscore';
import { Icon, Button, message } from 'antd';
import { PublishContainer, PublishList, ToolbarView, ListView } from '../styles';
import { AppStoreContext } from '../../../context/appStoreContext';
import { ShortMsgTextContext } from '../../../context/shortMessageContext';
import ShortMessageOnComplete from './ForApprovalAndPublish';
import {
  DraftIcon,
  PublishVersionIcon,
  NotYetTestedIcon,
  ForApprovalIcon,
  PublishIcon,
} from '../../../images/svgIcons';
import {
  getTestingVersions,
  getPublishedVersions,
  getTemplate,
  qaComplete,
  publishTemplate,
} from '../../../utils/services/shortMessageTemplateAPI';
import { useTranslation } from 'react-i18next';
import { SHORT_MESSAGE_TESTING_AND_PUBLISH_ENV } from './translationValues';

/* This component contains the complete publish cycle from sending temp to test 
  to published template details
  Child Components: 
    * ShortMessageOnComplete
*/

const NAMESPACES = ['SHORT_MESSAGE_TESTING_AND_PUBLISH_ENV'];

const PublishOverlay = props => {
  const {
    onCancel,
    onFetchingDetails,
    templateId,
    handleResponse,
    isNotYetSend,
    inTestEnvChannel,
    pubishedChannel,
    changeCurrentEnv,
    draftUpdatedOn,
  } = props;
  const { checkUserAccessRoles } = useContext(AppStoreContext);
  const { t } = useTranslation(NAMESPACES);
  const { currentEnv, setCurrentEnv } = useContext(ShortMsgTextContext);
  const [onReqAppModal, setOnReqAppModal] = useState(false);
  const [onPublishModal, setOnPublishModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [draftDate, setDraftDate] = useState('');
  const [testingEnvData, setTestingEnvData] = useState({});
  const [publishEnvData, setPublishEnvData] = useState({});
  const [testingDate, setTestingDate] = useState(
    t(
      SHORT_MESSAGE_TESTING_AND_PUBLISH_ENV.NOT_AVAILABLE.key,
      SHORT_MESSAGE_TESTING_AND_PUBLISH_ENV.NOT_AVAILABLE.value,
    ),
  );
  const [publishedDate, setPublishedDate] = useState(
    t(
      SHORT_MESSAGE_TESTING_AND_PUBLISH_ENV.NOT_YET_PUBLISHED.key,
      SHORT_MESSAGE_TESTING_AND_PUBLISH_ENV.NOT_YET_PUBLISHED.value,
    ),
  );
  const [isQaTesting, setIsQaTesting] = useState(false);
  const [isQaComplete, setIsQaComplete] = useState(false);
  const [dataByEnv, setDataByEnv] = useState(undefined);
  const [qareadyId, setQaReadyId] = useState(undefined);
  const publishAccess = checkUserAccessRoles(['PUBLISHER']);

  useEffect(() => {
    setCurrentEnv('testing');
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (draftUpdatedOn) setDraftDate(draftUpdatedOn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftUpdatedOn]);

  useEffect(() => {
    if (currentEnv) changeCurrentEnv(currentEnv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEnv]);

  useEffect(() => {
    setIsQaTesting(inTestEnvChannel?.length > 0);
    setIsQaComplete(pubishedChannel?.length > 0);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inTestEnvChannel, pubishedChannel]);

  useEffect(() => {
    let envData = onPublishModal ? pubishedChannel : onReqAppModal ? inTestEnvChannel : '';
    setDataByEnv(envData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onPublishModal, onReqAppModal]);

  const fetchData = () => {
    onFetchingDetails(true);
    getTestingData();
    getPublishedData();
  };

  const getDraftData = async () => {
    let draftData = await getTemplate(templateId);
    if (draftData && draftData.dataCount !== 0) {
      onCancel(draftData);
    }
  };

  const getTestingData = async () => {
    let testingData = await getTestingVersions(templateId);
    if (testingData) {
      setTestingEnvData(testingData);
      if (testingData.dataCount !== 0) {
        setTestingDate(testingData.data[0].createdOn);
        setQaReadyId(testingData.data[0].id);
        handleResponse(testingData);
      } else {
        /// testing: if TEMPLATE NOT YET SEND FOR TESTING and
        /// publish: if TEMPLATE NOT YET PUBLISHED
        isNotYetSend('testing');
        onFetchingDetails(false);
      }
    }
  };

  const getPublishedData = async () => {
    let publishedData = await getPublishedVersions(templateId);
    if (publishedData) {
      setPublishEnvData(publishedData);
      if (publishedData.dataCount !== 0) {
        setPublishedDate(publishedData.data[0].createdOn);
      }
    }
  };

  const requestForApproval = async newList => {
    setIsLoading(true);
    let response;
    let allResponses = await Promise.all(
      newList.map(async li => {
        response = await qaComplete(qareadyId, li);
        if (response) {
          setIsLoading(false);
        }
      }),
    );
    if (allResponses) {
      if (response && response.errors && response.errors.length == 0) {
        setIsLoading(false);
        message.success(
          t(
            SHORT_MESSAGE_TESTING_AND_PUBLISH_ENV.TEMPLATE_SEND_FOR_APPROVAL_SUCCESS_MESSAGE.key,
            SHORT_MESSAGE_TESTING_AND_PUBLISH_ENV.TEMPLATE_SEND_FOR_APPROVAL_SUCCESS_MESSAGE.value,
          ),
        );
        setOnReqAppModal(false);
        fetchData('testing');
      }
    }
  };
  const publishChannels = async publish => {
    setIsLoading(true);
    let currentPath = window.location.href;
    const id = currentPath.split('/').pop();
    let response = await publishTemplate(id, publish);
    if (response) {
      setIsLoading(false);
      if (response && response.errors && response.errors.length > 0) {
        message.error(response.status);
      } else {
        message.success(
          t(
            SHORT_MESSAGE_TESTING_AND_PUBLISH_ENV.TEMPLATE_PUBLISHED_SUCCESS_MESSAGE.key,
            SHORT_MESSAGE_TESTING_AND_PUBLISH_ENV.TEMPLATE_PUBLISHED_SUCCESS_MESSAGE.value,
          ),
        );
        setOnPublishModal(false);
        fetchData('testing');
      }
    }
  };

  const onEnvChange = env => {
    setCurrentEnv(env);
    onFetchingDetails(true);
    if (env === 'draft') {
      getDraftData();
    } else if (env === 'testing') {
      if (testingEnvData?.dataCount !== 0) {
        handleResponse(testingEnvData);
      } else {
        isNotYetSend('testing');
        onFetchingDetails(false);
      }
    } else if (env === 'publish') {
      if (publishEnvData?.dataCount !== 0) {
        handleResponse(publishEnvData);
      } else {
        isNotYetSend('publish');
        onFetchingDetails(false);
      }
    }
  };

  const renderList = (type, label, icon) => {
    return (
      <ListView activeVersion={currentEnv === type} onClick={() => onEnvChange(type)}>
        <li>
          <Icon component={icon} />
          <div className="column">
            <span>{label}</span>
            <span className={currentEnv === type ? 'note-text active' : 'note-text'}>
              {type === 'testing' && testingDate}
              {type === 'publish' && publishedDate}
              {type === 'draft' && draftDate}
            </span>
          </div>
        </li>
      </ListView>
    );
  };

  return (
    <>
      <PublishContainer shortMessage={true}>
        <PublishList shortMessage={true}>
          {renderList(
            'publish',
            t(
              SHORT_MESSAGE_TESTING_AND_PUBLISH_ENV.PUBLISHED_ENV_TAB.key,
              SHORT_MESSAGE_TESTING_AND_PUBLISH_ENV.PUBLISHED_ENV_TAB.value,
            ),
            PublishVersionIcon,
          )}
          {renderList(
            'testing',
            t(
              SHORT_MESSAGE_TESTING_AND_PUBLISH_ENV.TESTING_ENV_TAB.key,
              SHORT_MESSAGE_TESTING_AND_PUBLISH_ENV.TESTING_ENV_TAB.value,
            ),
            NotYetTestedIcon,
          )}
          {renderList(
            'draft',
            t(
              SHORT_MESSAGE_TESTING_AND_PUBLISH_ENV.DRAFT_ENV_TAB.key,
              SHORT_MESSAGE_TESTING_AND_PUBLISH_ENV.DRAFT_ENV_TAB.value,
            ),
            DraftIcon,
          )}
        </PublishList>
      </PublishContainer>
      <ToolbarView>
        {currentEnv === 'testing' && isQaTesting && (
          <Button key="approval" onClick={() => setOnReqAppModal(true)}>
            <Icon component={ForApprovalIcon} />
            {t(
              SHORT_MESSAGE_TESTING_AND_PUBLISH_ENV.REQUEST_FOR_APPROVAL_BUTTON.key,
              SHORT_MESSAGE_TESTING_AND_PUBLISH_ENV.REQUEST_FOR_APPROVAL_BUTTON.value,
            )}
          </Button>
        )}
        {currentEnv === 'testing' && publishAccess && isQaComplete && (
          <Button type="primary" key="publish" onClick={() => setOnPublishModal(true)}>
            <Icon component={PublishIcon} />
            {t(
              SHORT_MESSAGE_TESTING_AND_PUBLISH_ENV.PUBLISH_BUTTON.key,
              SHORT_MESSAGE_TESTING_AND_PUBLISH_ENV.PUBLISH_BUTTON.value,
            )}
          </Button>
        )}
        <Icon
          type="close"
          onClick={() => {
            onEnvChange('draft');
          }}
        />
      </ToolbarView>

      {onReqAppModal && (
        <ShortMessageOnComplete
          testingStatus={dataByEnv}
          onCancel={() => setOnReqAppModal(false)}
          requestForApproval={requestForApproval}
          isLoading={isLoading}
        />
      )}

      {onPublishModal && (
        <ShortMessageOnComplete
          testingStatus={dataByEnv}
          onCancel={() => setOnPublishModal(false)}
          requestForApproval={publishChannels}
          isLoading={isLoading}
          isPublish={true}
        />
      )}
    </>
  );
};

export default PublishOverlay;
